import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import z from 'zod';

import { DeliveryAddressResponse, GenerateAwbRequest } from '@/api/data-contracts';

export const toNumber = (value: number | string | undefined | null): number | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const normalized = value.replaceAll('.', '').replaceAll(',', '.').trim();
  if (normalized === '') return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const numericInput = z.union([z.number(), z.string()]);

const requiredPositive = (t: TFunction) =>
  numericInput.refine((value) => (toNumber(value) ?? 0) > 0, t('errors.must_be_greater_than_zero'));

const optionalAmount = (t: TFunction, max: number) =>
  numericInput.optional().refine(
    (value) => {
      if (value === undefined || value === '') return true;
      const parsed = toNumber(value);
      return parsed !== undefined && parsed >= 0 && parsed <= max;
    },
    t('errors.value_out_of_range', { min: 0, max }),
  );

const MAX_COUNT = 999;
const MIN_DIMENSION = 0.1;

const DIMENSION_FIELDS = ['length', 'width', 'height'] as const;

const optionalCount = (t: TFunction) =>
  numericInput.optional().refine(
    (value) => {
      if (value === undefined || value === '') return true;
      const parsed = toNumber(value);
      return parsed !== undefined && Number.isInteger(parsed) && parsed >= 0 && parsed <= MAX_COUNT;
    },
    t('errors.value_out_of_range', { min: 0, max: MAX_COUNT }),
  );

const optionalDimension = (t: TFunction) =>
  numericInput.optional().refine(
    (value) => {
      if (value === undefined || value === '') return true;
      return (toNumber(value) ?? 0) >= MIN_DIMENSION;
    },
    t('errors.min_dimension', { min: MIN_DIMENSION }),
  );

const phoneFormat = /^\+?[0-9][0-9 ./-]{3,14}[0-9]$/;

const optionalText = (max: number, t: TFunction) =>
  z.string().max(max, t('errors.field_too_long')).optional();

export const createAwbFormSchema = (t: TFunction) =>
  z
    .object({
      clientId: z.string().min(1, t('errors.required_field')),
      deliveryAddressId: z.string().optional(),

      recipientName: z
        .string()
        .trim()
        .min(1, t('errors.required_field'))
        .max(50, t('errors.field_too_long')),
      contactPerson: z.string().trim().max(50, t('errors.field_too_long')),
      phone: z
        .string()
        .trim()
        .min(1, t('errors.required_field'))
        .max(16, t('errors.field_too_long'))
        .regex(phoneFormat, t('errors.invalid_phone_format')),
      email: z
        .string()
        .trim()
        .max(100, t('errors.field_too_long'))
        .refine(
          (value) => !value || z.string().email().safeParse(value).success,
          t('errors.invalid_email'),
        ),
      county: z.string().min(1, t('errors.required_field')),
      locality: z.string().min(1, t('errors.required_field')),
      street: z.string().min(1, t('errors.required_field')).max(255, t('errors.field_too_long')),

      secondaryPhone: z
        .string()
        .max(16, t('errors.field_too_long'))
        .refine((value) => !value || phoneFormat.test(value), t('errors.invalid_phone_format'))
        .optional(),
      streetNo: optionalText(10, t),
      zipCode: z
        .string()
        .refine(
          (value) => !value || (value.length >= 5 && value.length <= 6),
          t('errors.invalid_zip_code'),
        )
        .optional(),
      building: optionalText(40, t),
      entrance: optionalText(16, t),
      floor: optionalText(10, t),
      apartment: optionalText(10, t),

      service: z.string().min(1, t('errors.required_field')),
      weight: requiredPositive(t),
      length: optionalDimension(t),
      width: optionalDimension(t),
      height: optionalDimension(t),
      intendedPickupDate: z.string().min(1, t('errors.required_field')),
      parcels: optionalCount(t),
      envelopes: optionalCount(t),
      declaredValue: optionalAmount(t, 999999),
      options: z.array(z.string()),
      observation: optionalText(255, t),
      content: optionalText(255, t),
    })
    .superRefine((values, ctx) => {
      if (packagesMissing(values)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['parcels'],
          message: t('errors.awb_packages_required'),
        });
      }

      if (!dimensionsRequired(values)) return;

      DIMENSION_FIELDS.forEach((field) => {
        if ((toNumber(values[field]) ?? 0) > 0) return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: t('errors.required_field'),
        });
      });
    });

export type AwbFormValues = z.infer<ReturnType<typeof createAwbFormSchema>>;

export const packagesMissing = (values: {
  parcels?: number | string;
  envelopes?: number | string;
}): boolean => (toNumber(values.parcels) ?? 0) + (toNumber(values.envelopes) ?? 0) <= 0;

export const dimensionsRequired = (values: {
  parcels?: number | string;
  length?: number | string;
  width?: number | string;
  height?: number | string;
}): boolean =>
  (toNumber(values.parcels) ?? 0) > 0 ||
  DIMENSION_FIELDS.some((field) => (toNumber(values[field]) ?? 0) > 0);

export const nextWorkingDay = (from = dayjs()): string => {
  let date = from.add(1, 'day');
  while (date.day() === 0 || date.day() === 6) {
    date = date.add(1, 'day');
  }
  return date.format('YYYY-MM-DD');
};

export const AWB_RECIPIENT_FIELDS = [
  'recipientName',
  'contactPerson',
  'phone',
  'email',
  'county',
  'locality',
  'street',
] as const;

export const resolveDefaultClientId = (
  pickupPoints: { fanClientId: string; isDefault: boolean }[],
): string =>
  pickupPoints.find((point) => point.isDefault)?.fanClientId ?? pickupPoints[0]?.fanClientId ?? '';

export const getAwbFormDefaults = (clientId?: string): AwbFormValues => ({
  clientId: clientId ?? '',
  deliveryAddressId: '',
  recipientName: '',
  contactPerson: '',
  phone: '',
  email: '',
  county: '',
  locality: '',
  street: '',
  secondaryPhone: '',
  streetNo: '',
  zipCode: '',
  building: '',
  entrance: '',
  floor: '',
  apartment: '',
  service: 'Standard',
  weight: '',
  length: '',
  width: '',
  height: '',
  intendedPickupDate: nextWorkingDay(),
  parcels: 1,
  envelopes: 0,
  declaredValue: '',
  options: [],
  observation: '',
  content: '',
});

export const deliveryAddressToFormValues = (
  address: DeliveryAddressResponse,
): Pick<AwbFormValues, (typeof AWB_RECIPIENT_FIELDS)[number] | 'deliveryAddressId'> &
  Partial<AwbFormValues> => ({
  deliveryAddressId: address.id,
  recipientName: address.recipientName ?? '',
  contactPerson: address.contactPerson ?? '',
  phone: address.phone ?? '',
  email: address.email ?? '',
  county: address.county ?? '',
  locality: address.locality ?? '',
  street: address.street ?? '',
  secondaryPhone: address.secondaryPhone ?? '',
  streetNo: address.streetNo ?? '',
  zipCode: address.zipCode ?? '',
  building: address.building ?? '',
  entrance: address.entrance ?? '',
  floor: address.floor ?? '',
  apartment: address.apartment ?? '',
  observation: address.observations ?? '',
});

const trimmed = (value: string | undefined): string | undefined => {
  const next = value?.trim();
  return next ? next : undefined;
};

export const awbFormToRequest = (
  values: AwbFormValues,
  fanLocalityId: number | undefined,
  quotedTotal: number | undefined,
): GenerateAwbRequest => {
  const dimensions = dimensionsRequired(values)
    ? {
        length: toNumber(values.length)!,
        width: toNumber(values.width)!,
        height: toNumber(values.height)!,
      }
    : ({} as Pick<GenerateAwbRequest, 'length' | 'width' | 'height'>);

  return {
    recipient: {
      recipientName: values.recipientName.trim(),
      contactPerson: trimmed(values.contactPerson),
      phone: values.phone.trim(),
      email: trimmed(values.email),
      county: values.county,
      locality: values.locality,
      street: values.street,
      secondaryPhone: trimmed(values.secondaryPhone),
      fanLocalityId,
      streetNo: trimmed(values.streetNo),
      zipCode: trimmed(values.zipCode),
      building: trimmed(values.building),
      entrance: trimmed(values.entrance),
      floor: trimmed(values.floor),
      apartment: trimmed(values.apartment),
    },
    service: values.service,
    parcels: toNumber(values.parcels) ?? 0,
    envelopes: toNumber(values.envelopes) ?? 0,
    weight: toNumber(values.weight)!,
    ...dimensions,
    intendedPickupDate: dayjs(values.intendedPickupDate).format('YYYY-MM-DD'),
    clientId: trimmed(values.clientId),
    deliveryAddressId: trimmed(values.deliveryAddressId),
    declaredValue: toNumber(values.declaredValue),
    options: values.options,
    observation: trimmed(values.observation),
    content: trimmed(values.content),
    quotedTotal,
  };
};

export const AWB_RECIPIENT_STEP = 1;
export const AWB_PARCEL_STEP = 2;

export const AWB_STEP_FIELDS: (keyof AwbFormValues)[][] = [
  ['clientId'],
  [
    'recipientName',
    'contactPerson',
    'phone',
    'email',
    'county',
    'locality',
    'street',
    'secondaryPhone',
    'streetNo',
    'zipCode',
    'building',
    'entrance',
    'floor',
    'apartment',
  ],
  [
    'service',
    'intendedPickupDate',
    'weight',
    'length',
    'width',
    'height',
    'parcels',
    'envelopes',
    'declaredValue',
    'options',
    'content',
    'observation',
  ],
  [],
];
